import React, { useState, useEffect } from 'react';
import {
  Paper, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableRow, TableHead, Alert, Typography,
  Switch, FormControlLabel, Box
} from '@mui/material';
import axiosInstance from '../utills/axiosInstance';
import { usePermissions } from '../utills/usePermissions';

const PayrollComponents = () => {
  const [components, setComponents] = useState([]);
  const [form, setForm] = useState({
    name: '',
    type: 'earning',
    isFixed: true,
    value: 0,
    isTaxable: true,
    basedOn: 'gross',
    cap: 0,
    max: 0,
    min: 0,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { canCreate, canEdit, hasPermission } = usePermissions();

  useEffect(() => {
    fetchComponents();
  }, []);

  const normalizeName = (name) => name.toLowerCase().replace(/\s+/g, '_');

  const fetchComponents = async () => {
    try {
      const res = await axiosInstance.get('/api/payroll/get-components');
      setForm({ name: '', type: 'earning', isFixed: true, value: 0, isTaxable: true, basedOn: 'gross', cap: 0, max: 0, min: 0 });
      setComponents(res?.data.data || []);
    } catch (err) {
      setError('Failed to load components');
    }
  };

  const earningComponents = components.filter(c => c.type === 'earning');
  const basedOnOptions = ['gross', ...earningComponents.map(c => normalizeName(c.name))];

  const handleSubmit = async () => {
    try {
      await axiosInstance.post('/api/payroll/components', form);
      setSuccess('Component added');
      setForm({ name: '', type: 'earning', isFixed: true, value: 0, isTaxable: true, basedOn: 'gross', cap: 0, max: 0, min: 0, min: 0 });
      fetchComponents();
    } catch (err) {
      setError('Failed to add component');
    }
  };

  const handleEdit = async (id) => {
    const comp = components?.find(c => c._id === id);
    setForm({ ...comp });
  };

  const handleUpdate = async () => {
    try {
      await axiosInstance.put(`/api/payroll/components/${form._id}`, form);
      setSuccess('Component updated');
      setForm({ name: '', type: 'earning', isFixed: true, value: 0, isTaxable: true, basedOn: 'gross', cap: 0, max: 0, min: 0 });
      fetchComponents();
    } catch (err) {
      setError('Failed to update component');
    }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await axiosInstance.patch(`/api/payroll/components/${id}/activate`, { isActive });
      setSuccess('Component toggled');
      fetchComponents();
    } catch (err) {
      setError('Failed to toggle component');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Payroll Components</Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {
        (canCreate('payrollComponents') || (canEdit('payrollComponents') && form._id)) && (
          <Box sx={{ mb: 3, mt: 3 }}>
            <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mr: 2, mb: 2 }} />
            <FormControl sx={{ mr: 2, minWidth: 120, mb: 2 }}>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <MenuItem value="earning">Earning</MenuItem>
                <MenuItem value="deduction">Deduction</MenuItem>
                <MenuItem value="statutory">Statutory</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={form.isFixed} onChange={(e) => setForm({ ...form, isFixed: e.target.checked })} />}
              label="Fixed Amount"
            />
            <TextField
              label={form.isFixed ? "Value (₹)" : "Percentage (%)"}
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              sx={{ mr: 2, mb: 2 }}
              inputProps={{ min: 0, step: form.isFixed ? 100 : 0.1 }}
            />
            <FormControl sx={{ mr: 2, minWidth: 120, mb: 2 }}>
              <InputLabel>Based On</InputLabel>
              <Select label="Based On" value={form.basedOn} onChange={(e) => setForm({ ...form, basedOn: e.target.value })}>
                {basedOnOptions.map(option => (
                  <MenuItem key={option} value={option}>{option.replace('_', ' ').toUpperCase()}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Cap (₹)" type="number" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} sx={{ mr: 2, mb: 2 }} placeholder="e.g., 15000 for PF" />
            <TextField label="Max (₹)" type="number" value={form.max} onChange={(e) => setForm({ ...form, max: e.target.value })} sx={{ mr: 2, mb: 2 }} placeholder="e.g., 21000 for ESIC" />
            <TextField label="Min (₹)" type="number" value={form.min} onChange={(e) => setForm({ ...form, min: e.target.value })} sx={{ mr: 2, mb: 2 }} placeholder="e.g., 21000 for ESIC" />
            <FormControlLabel
              control={<Switch checked={form.isTaxable} onChange={(e) => setForm({ ...form, isTaxable: e.target.checked })} />}
              label="Taxable"
            />
            <Button variant="contained" onClick={form._id ? handleUpdate : handleSubmit}>
              {form._id ? 'Update' : 'Add'}
            </Button>
          </Box>
        )
      }

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Value</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Based On</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Cap (₹)</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Max (₹)</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Min (₹)</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Active</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {components?.map(c => (
            <TableRow key={c._id}>
              <TableCell>{c.name}</TableCell>
              <TableCell>{c.type}</TableCell>
              <TableCell>{c.isFixed ? '₹' : ''}{c.value}{c.isFixed ? '' : '%'}</TableCell>
              <TableCell>{c.basedOn}</TableCell>
              <TableCell>₹{c.cap}</TableCell>
              <TableCell>₹{c.max}</TableCell>
              <TableCell>₹{c.min}</TableCell>
              <TableCell>
                {
                  hasPermission("payrollComponents:activate") && (
                    <Switch checked={c.isActive} onChange={() => handleToggle(c._id, !c.isActive)} />
                  )
                  
                }
              </TableCell>
              <TableCell>
              {
                canEdit('payrollComponents') && (
                  <Button onClick={() => handleEdit(c._id)}>Edit</Button>
                )
              }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default PayrollComponents;
